/** @format */
import {
  type QueryClient,
  type UseQueryOptions,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { ColumnTable } from "arquero";
import { keyBy, mapValues } from "lodash";
import { useMemo } from "react";
import { useOptionalPlaybackState } from "@/context/playbackContext";
import galaxieClient, { ARROW_MIME_TYPE, isColumnTable } from "./galaxieClient";
import { QUERY_CONFIG } from "./queryConfig";
import { usePlaybackQuery } from "./usePlaybackQuery";

const refetchInterval = (isLive: boolean) => (isLive ? 20 * 1000 : 60 * 5 * 1000);

/**
 * Query options factories for runs
 * These factories provide queryKey + queryFn together for reuse in both hooks and loaders
 * Single source of truth for endpoints - eliminates duplication in route loaders
 */
export const runQueryOptions = {
  live: () => ({
    queryKey: ["liveRuns"] as const,
    queryFn: async () => {
      const response = await galaxieClient.get("/live/");
      return {
        data: response.data as RunDetail[],
        version: response.headers["x-version"] as string,
      };
    },
  }),

  detail: (runID: string) => ({
    queryKey: ["run", runID, "detail", "live"] as const,
    queryFn: async () => {
      const response = await galaxieClient.get(`/runs/${runID}/`);
      return response.data as RunDetail;
    },
  }),

  vehicles: (runID: string) => ({
    queryKey: ["run", runID, "vehicles", "live"] as const,
    queryFn: async () => {
      const response = await galaxieClient.get(`/runs/${runID}/vehicles/`);
      return response.data as Vehicle[];
    },
  }),
};

interface LiveRunResponse {
  data: RunDetail[];
  version: string;
}

const getLiveRun = (): Promise<LiveRunResponse> =>
  galaxieClient.get("/live/").then(res => ({
    data: res.data as RunDetail[],
    version: res.headers["x-version"] as string,
  }));

const useLiveRun_internal = <TQueryFnData = LiveRunResponse>(
  options = {}
): UseQueryResult<TQueryFnData> =>
  useQuery<LiveRunResponse, Error, TQueryFnData>({
    queryKey: ["liveRuns"],
    queryFn: getLiveRun,
    refetchIntervalInBackground: false, // Disable background refetching
    placeholderData: { data: [], version: "" } as LiveRunResponse,
    ...options,
  });

export const useLiveRun = <TQueryFnData = RunDetail[]>(
  options = {}
): UseQueryResult<TQueryFnData> => {
  const { data, ...rest } = useLiveRun_internal<{
    data: TQueryFnData;
    version: string;
  }>(options);
  return { data: data?.data ?? [], ...rest } as UseQueryResult<TQueryFnData>;
};

/**
 * Return `data` only if it is a real Arquero ColumnTable. The galaxieClient
 * interceptor converts Arrow responses to a ColumnTable; if a non-Arrow response
 * slips through (error body, empty payload, wrong content-type) `res.data` is a
 * raw ArrayBuffer, and blindly casting it `as ColumnTable` produced a fake table
 * that later crashed with "f.objects is not a function"
 * (JAVASCRIPT-REACT-H9 / H8 / ED). Throw here so the query surfaces the anomaly
 * and the bad value never enters the cache.
 */
const asArrowTable = (data: unknown, resource: string): ColumnTable => {
  if (isColumnTable(data)) return data;
  throw new Error(
    `Expected an Apache Arrow table for "${resource}" but received ${
      data === null ? "null" : typeof data
    }`
  );
};

export function useRun<TQueryFnData = RunDetail>(
  runID: string,
  options: Omit<
    UseQueryOptions<RunDetail, Error, TQueryFnData>,
    "queryKey" | "queryFn"
  > = {}
): UseQueryResult<TQueryFnData> {
  const playback = useOptionalPlaybackState();

  return usePlaybackQuery<RunDetail, TQueryFnData>({
    runID,
    playback,
    resource: "detail",
    fetchFn: async ({ runID, playback: playbackState }) =>
      galaxieClient
        .get(`/runs/${runID}/`, {
          params:
            playbackState.mode !== "live" && playbackState.lap !== null
              ? { lap: playbackState.lap }
              : undefined,
        })
        .then(res => res.data as RunDetail),
    options: {
      ...(playback.mode === "live"
        ? QUERY_CONFIG.WEBSOCKET_DATA
        : QUERY_CONFIG.PLAYBACK_DATA),
      ...options,
    },
  });
}

export const useUpdateRun = (runID: string) => {
  const queryClient = useQueryClient();
  return useMutation<RunDetail, Error, Partial<RunDetail>>({
    mutationFn: async run =>
      await galaxieClient
        .patch(`/runs/${runID}/`, run)
        .then(res => res.data as RunDetail),
    onMutate: run => {
      const key = ["run", runID, "detail", "live"] as const;
      const oldRun = queryClient.getQueryData<RunDetail>(key);
      queryClient.setQueryData(key, {
        ...oldRun,
        ...run,
      });
      return oldRun;
    },
    onSuccess: (data, _run) => {
      queryClient.setQueryData(["run", runID, "detail", "live"], data);
    },
    onError: (_err, _variables, run) => {
      queryClient.setQueryData(["run", runID, "detail", "live"], run);
    },
  });
};

export const useServerVersion = () => {
  const { data, ...rest } = useLiveRun_internal<LiveRunResponse>();
  return { data: data?.version, ...rest };
};

export const useIsLiveRun = (runID: string, options = {}): boolean => {
  const { data = [] } = useLiveRun({ ...options });
  const liveIDs = data?.map((run: RunDetail) => run.id);
  return useMemo(() => liveIDs?.includes(runID), [liveIDs, runID]);
};

export function useWeather<TQueryFnData = Weather>(
  runID: string,
  options: Omit<
    UseQueryOptions<Weather, Error, TQueryFnData>,
    "queryKey" | "queryFn"
  > = {}
): UseQueryResult<TQueryFnData> {
  const isLive = useIsLiveRun(runID);
  return useQuery<Weather, Error, TQueryFnData>({
    queryKey: ["run", runID, "weather"],
    queryFn: async () =>
      await galaxieClient
        .get(`/runs/${runID}/weather/`)
        .then(res => res.data as Weather),
    // Weather is NOT updated via WebSocket, so keep polling
    refetchInterval: refetchInterval(isLive), // 20s for live, 5min for non-live
    refetchIntervalInBackground: false, // Disable background refetching
    staleTime: isLive ? 15 * 1000 : Infinity, // 15 seconds for live (less than refetchInterval)
    ...options,
    enabled: !!runID && runID !== "undefined" && (options?.enabled ?? true),
  });
}

export function useGRCupSectorTimes<TQueryFnData = ColumnTable>(
  runID: string,
  {
    vehicleId,
  }: {
    vehicleId?: string | null;
  } = {},
  options: Omit<
    UseQueryOptions<ColumnTable, Error, TQueryFnData>,
    "queryKey" | "queryFn"
  > = {}
): UseQueryResult<TQueryFnData> {
  const playback = useOptionalPlaybackState();
  const vehicleKey = vehicleId ?? "all";

  return usePlaybackQuery<ColumnTable, TQueryFnData>({
    runID,
    playback,
    resource: ["grcup", "sector_times", vehicleKey],
    fetchFn: async ({ runID, playback: playbackState }) =>
      galaxieClient
        .get(`/runs/${runID}/grcup/sector_times/`, {
          params: {
            ...(vehicleId ? { vehicle_id: vehicleId } : {}),
            ...(playbackState.mode !== "live" && playbackState.lap !== null
              ? { lap: playbackState.lap }
              : {}),
          },
          headers: { Accept: ARROW_MIME_TYPE },
          responseType: "arraybuffer",
        })
        .then(res => asArrowTable(res.data, "grcup_sector_times")),
    options: {
      ...(playback.mode === "live"
        ? QUERY_CONFIG.WEBSOCKET_DATA
        : QUERY_CONFIG.PLAYBACK_DATA),
      structuralSharing: false, // ColumnTable loses prototype methods with replaceEqualDeep
      ...options,
    },
  });
}

export function useGRCupWeatherReadings<TQueryFnData = GRCupWeatherResponse>(
  runID: string,
  options: Omit<
    UseQueryOptions<GRCupWeatherResponse, Error, TQueryFnData>,
    "queryKey" | "queryFn"
  > = {}
): UseQueryResult<TQueryFnData> {
  const isLive = useIsLiveRun(runID);
  return useQuery<GRCupWeatherResponse, Error, TQueryFnData>({
    queryKey: ["run", runID, "grcup", "weather"],
    queryFn: async () =>
      await galaxieClient
        .get(`/runs/${runID}/grcup/weather/`)
        .then(res => res.data as GRCupWeatherResponse),
    // GR Cup weather is NOT updated via WebSocket, so keep polling
    refetchInterval: refetchInterval(isLive), // 20s for live, 5min for non-live
    refetchIntervalInBackground: false, // Disable background refetching
    staleTime: isLive ? 15 * 1000 : Infinity, // 15 seconds for live (less than refetchInterval)
    ...options,
    enabled: !!runID && runID !== "undefined" && (options?.enabled ?? true),
  });
}

export function useVehiclesList<TQueryFnData = Vehicle[]>(
  runID: string,
  options: Omit<
    UseQueryOptions<Vehicle[], Error, TQueryFnData>,
    "queryKey" | "queryFn"
  > = {}
): UseQueryResult<TQueryFnData> {
  const playback = useOptionalPlaybackState();

  return usePlaybackQuery<Vehicle[], TQueryFnData>({
    runID,
    playback,
    resource: "vehicles",
    fetchFn: async ({ runID, playback: playbackState }) =>
      galaxieClient
        .get(`/runs/${runID}/vehicles/`, {
          params:
            playbackState.mode !== "live" && playbackState.lap !== null
              ? { lap: playbackState.lap }
              : undefined,
        })
        .then(res => res.data as Vehicle[]),
    options: {
      ...(playback.mode === "live"
        ? QUERY_CONFIG.WEBSOCKET_DATA
        : QUERY_CONFIG.PLAYBACK_DATA),
      ...options,
    },
  });
}

export function useVehicleLapsList<TQueryFnData = ColumnTable>(
  runID: string,
  options: Omit<
    UseQueryOptions<ColumnTable, Error, TQueryFnData>,
    "queryKey" | "queryFn"
  > = {}
): UseQueryResult<TQueryFnData> {
  const playback = useOptionalPlaybackState();

  return usePlaybackQuery<ColumnTable, TQueryFnData>({
    runID,
    playback,
    resource: "vehicle_laps",
    fetchFn: async ({ runID, playback: playbackState }) =>
      galaxieClient
        .get(`/runs/${runID}/vehicle_laps/`, {
          params:
            playbackState.mode !== "live" && playbackState.lap !== null
              ? { lap: playbackState.lap }
              : undefined,
          headers: { Accept: ARROW_MIME_TYPE },
          responseType: "arraybuffer",
        })
        .then(res => asArrowTable(res.data, "vehicle_laps")),
    options: {
      structuralSharing: false, // ColumnTable loses prototype methods with replaceEqualDeep
      ...(playback.mode === "live"
        ? QUERY_CONFIG.WEBSOCKET_DATA
        : QUERY_CONFIG.PLAYBACK_DATA),
      ...options,
    },
  });
}

export function useLapTimeFalloff<TQueryFnData = ColumnTable>(
  runID: string,
  options: Omit<
    UseQueryOptions<ColumnTable, Error, TQueryFnData>,
    "queryKey" | "queryFn"
  > = {}
): UseQueryResult<TQueryFnData> {
  const playback = useOptionalPlaybackState();

  return usePlaybackQuery<ColumnTable, TQueryFnData>({
    runID,
    playback,
    resource: "lap_time_falloff",
    fetchFn: async ({ runID, playback: playbackState }) =>
      galaxieClient
        .get(`/runs/${runID}/lap_time_falloff/`, {
          params:
            playbackState.mode !== "live" && playbackState.lap !== null
              ? { lap: playbackState.lap }
              : undefined,
          headers: { Accept: ARROW_MIME_TYPE },
          responseType: "arraybuffer",
        })
        .then(res => asArrowTable(res.data, "lap_time_falloff")),
    options: {
      structuralSharing: false, // ColumnTable loses prototype methods with replaceEqualDeep
      ...(playback.mode === "live"
        ? QUERY_CONFIG.WEBSOCKET_DATA
        : QUERY_CONFIG.PLAYBACK_DATA),
      ...options,
    },
  });
}

export function useVehiclesDetail<TQueryFnData = VehicleDetail>(
  runID: string,
  vehicleID: string | null,
  options: Omit<
    UseQueryOptions<VehicleDetail, Error, TQueryFnData>,
    "queryKey" | "queryFn"
  > = {}
): UseQueryResult<TQueryFnData> {
  const playback = useOptionalPlaybackState();

  return usePlaybackQuery<VehicleDetail, TQueryFnData>({
    runID,
    playback,
    resource: ["vehicles", vehicleID ?? "detail"],
    fetchFn: async ({ runID, playback: playbackState }) => {
      return galaxieClient
        .get(`/runs/${runID}/vehicles/${vehicleID}/`, {
          params:
            playbackState.mode !== "live" && playbackState.lap !== null
              ? { lap: playbackState.lap }
              : undefined,
        })
        .then(res => res.data as VehicleDetail);
    },
    options: {
      enabled: !!vehicleID,
      ...(playback.mode === "live"
        ? QUERY_CONFIG.WEBSOCKET_DATA
        : QUERY_CONFIG.PLAYBACK_DATA),
      ...options,
    },
  });
}

export function usePitStops<TQueryFnData = ColumnTable>(
  runID: string,
  options: Omit<
    UseQueryOptions<ColumnTable, Error, TQueryFnData>,
    "queryKey" | "queryFn"
  > = {}
): UseQueryResult<TQueryFnData> {
  const playback = useOptionalPlaybackState();

  return usePlaybackQuery<ColumnTable, TQueryFnData>({
    runID,
    playback,
    resource: "pitstops",
    fetchFn: async ({ runID, playback: playbackState }) =>
      galaxieClient
        .get(`/runs/${runID}/pitstops/`, {
          params:
            playbackState.mode !== "live" && playbackState.lap !== null
              ? { lap: playbackState.lap }
              : undefined,
          headers: { Accept: ARROW_MIME_TYPE },
          responseType: "arraybuffer",
        })
        .then(res => asArrowTable(res.data, "pitstops")),
    options: {
      structuralSharing: false, // ColumnTable loses prototype methods with replaceEqualDeep
      ...(playback.mode === "live"
        ? QUERY_CONFIG.WEBSOCKET_DATA
        : QUERY_CONFIG.PLAYBACK_DATA),
      ...options,
    },
  });
}

export function usePoints<TQueryFnData = ColumnTable>(
  runID: string,
  options: Omit<
    UseQueryOptions<ColumnTable, Error, TQueryFnData>,
    "queryKey" | "queryFn"
  > = {}
): UseQueryResult<TQueryFnData> {
  const playback = useOptionalPlaybackState();

  return usePlaybackQuery<ColumnTable, TQueryFnData>({
    runID,
    playback,
    resource: "points",
    fetchFn: async ({ runID, playback: playbackState }) =>
      galaxieClient
        .get(`/runs/${runID}/points/`, {
          params:
            playbackState.mode !== "live" && playbackState.lap !== null
              ? { lap: playbackState.lap }
              : undefined,
          headers: { Accept: ARROW_MIME_TYPE },
          responseType: "arraybuffer",
        })
        .then(res => asArrowTable(res.data, "points")),
    options: {
      structuralSharing: false, // ColumnTable loses prototype methods with replaceEqualDeep
      ...(playback.mode === "live"
        ? QUERY_CONFIG.WEBSOCKET_DATA
        : QUERY_CONFIG.PLAYBACK_DATA),
      ...options,
    },
  });
}

export function useGRatingForecast<TQueryFnData = GRatingForecastResponse>(
  runID: string,
  options: Omit<
    UseQueryOptions<GRatingForecastResponse, Error, TQueryFnData>,
    "queryKey" | "queryFn"
  > & {
    enabled?: boolean;
  } = {}
): UseQueryResult<TQueryFnData> {
  const playback = useOptionalPlaybackState();

  return usePlaybackQuery<GRatingForecastResponse, TQueryFnData>({
    runID,
    playback,
    resource: "g_rating_forecast",
    fetchFn: async ({ runID, playback: playbackState }) =>
      galaxieClient
        .get<GRatingForecastResponse>(`/runs/${runID}/g_rating_forecast/`, {
          params:
            playbackState.mode !== "live" && playbackState.lap !== null
              ? { lap: playbackState.lap }
              : undefined,
        })
        .then(res => res.data),
    options: {
      ...(playback.mode === "live"
        ? QUERY_CONFIG.WEBSOCKET_DATA
        : QUERY_CONFIG.PLAYBACK_DATA),
      ...options,
    },
  });
}

export function useStaffVehicleConfigs<TQueryFnData = VehicleConfig[]>(
  runID: string,
  options: Omit<
    UseQueryOptions<VehicleConfig[], Error, TQueryFnData>,
    "queryKey" | "queryFn"
  > = {}
): UseQueryResult<TQueryFnData> {
  return useQuery<VehicleConfig[], Error, TQueryFnData>({
    queryKey: ["run", runID, "vehicle_configs"],
    queryFn: async () =>
      await galaxieClient
        .get(`/runs/${runID}/vehicle_configs/`)
        .then(res => res.data as VehicleConfig[]),
    refetchInterval: false,
    ...options,
    enabled: !!runID && runID !== "undefined" && (options?.enabled ?? true),
  });
}

export const useUpdateColor = (runID: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    VehicleConfig,
    Error,
    Partial<VehicleConfig>,
    {
      previousVehicleConfigs: VehicleConfig[];
    }
  >({
    mutationFn: async ({ id, ...rest }) =>
      await galaxieClient
        .patch(`/runs/${runID}/vehicle_configs/${id}/`, { id, ...rest })
        .then(res => res.data as VehicleConfig),
    onMutate: ({ id, ...rest }) => {
      const previousVehicleConfigs = queryClient.getQueryData<
        VehicleConfig[] | undefined
      >(["run", runID, "vehicle_configs"]);

      if (previousVehicleConfigs) {
        queryClient.setQueryData(
          ["run", runID, "vehicle_configs"],
          (old: VehicleConfig[] | undefined) => {
            if (!old) {
              return previousVehicleConfigs;
            }
            return old.map(vehicle =>
              vehicle.id === id ? { ...vehicle, ...rest } : vehicle
            );
          }
        );
      }

      return {
        previousVehicleConfigs: previousVehicleConfigs ?? [],
      };
    },
    onSuccess: updatedVehicle => {
      // Update the specific vehicle with the actual response data
      queryClient.setQueryData(
        ["run", runID, "vehicle_configs"],
        (old: VehicleConfig[] | undefined) => {
          if (!old) {
            return old;
          }
          return old.map(vehicle =>
            vehicle.id === updatedVehicle.id ? updatedVehicle : vehicle
          );
        }
      );
    },
    onError: (_, __, context) => {
      // Rollback to previous state on error
      if (context?.previousVehicleConfigs) {
        queryClient.setQueryData(
          ["run", runID, "vehicle_configs"],
          context.previousVehicleConfigs
        );
      }
    },
  });
};

export const useDriverIDtoVehicleNumber = (
  runID: string,
  options: Omit<
    UseQueryOptions<Vehicle[], Error, Record<string, string>>,
    "queryKey" | "queryFn"
  > = {}
) =>
  useVehiclesList<Record<string, string>>(runID, {
    ...options,
    select: (vehicles: Vehicle[]) => {
      return mapValues(keyBy(vehicles || [], "driverId"), "vehicleNumber");
    },
  });

export const useVehicleNumbertoDriverID = (
  runID: string,
  options: Omit<
    UseQueryOptions<Vehicle[], Error, Record<string, string>>,
    "queryKey" | "queryFn"
  > = {}
) =>
  useVehiclesList<Record<string, string>>(runID, {
    ...options,
    select: (vehicles: Vehicle[]) => {
      return mapValues(keyBy(vehicles || [], "vehicleNumber"), "driverId");
    },
  });

export const useDriverIDtoNascarID = (
  runID: string,
  options: Omit<
    UseQueryOptions<Vehicle[], Error, Record<string, string>>,
    "queryKey" | "queryFn"
  > = {}
) =>
  useVehiclesList<Record<string, number>>(runID, {
    ...options,
    select: (vehicles: Vehicle[]) => {
      return mapValues(keyBy(vehicles, "driverId"), "driverNascarId");
    },
  });

export const useDriverIDtoVehicleID = (
  runID: string,
  options: Omit<
    UseQueryOptions<Vehicle[], Error, Record<string, string>>,
    "queryKey" | "queryFn"
  > = {}
) =>
  useVehiclesList<Record<string, string>>(runID, {
    ...options,
    select: (vehicles: Vehicle[]) => {
      return mapValues(keyBy(vehicles, "driverId"), "vehicleId");
    },
  });

export const useUploadRoster = (runID: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { file: File }) => {
      const formData = new FormData();
      formData.append("file", data.file, data.file.name);
      const response = await galaxieClient.post(
        `/runs/${runID}/colors/upload/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data as VehicleConfig[];
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["vehicleConfigs", runID] });
    },
  });
};

export const useVehicleCrewList = (runID: string) => {
  return useQuery<VehicleCrewResponse, Error, VehicleCrewResponse>({
    queryKey: ["run", runID, "vehicles", "crew"],
    queryFn: async () =>
      await galaxieClient
        .get(`/runs/${runID}/vehicle_crews/`)
        .then(res => res.data as VehicleCrewResponse),
    enabled: !!runID && runID !== "undefined",
  });
};

export const useCrewMemberProfile = (
  crewMemberId: string,
  vehicleId?: string,
  options?: { enabled?: boolean }
) => {
  return useQuery<CrewMemberProfile, Error, CrewMemberProfile>({
    queryKey: ["crew-member", crewMemberId, "profile", vehicleId ?? null],
    queryFn: async () =>
      await galaxieClient
        .get(`/crew_members/${crewMemberId}/profile/`, {
          // Resolve the cluster from the vehicle the modal was opened from,
          // not an arbitrary cluster the member belongs to.
          params: vehicleId ? { vehicle_id: vehicleId } : undefined,
        })
        .then(res => res.data as CrewMemberProfile),
    enabled: !!crewMemberId && options?.enabled !== false,
  });
};

// Staff-only mutation: flip whether display_name renders as
// "Nickname Lastname" (true) or "Firstname Lastname" (false).
export const useUpdateCrewMember = (crewMemberId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    {
      id: string;
      goesByNickname: boolean;
      displayName: string;
      fullName: string;
      nickname: string | null;
    },
    Error,
    { goesByNickname: boolean }
  >({
    mutationFn: async data => {
      const response = await galaxieClient.patch(
        `/crew_members/${crewMemberId}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate the profile + any VehicleCrewList that might embed
      // this member's displayName.
      void queryClient.invalidateQueries({
        queryKey: ["crew-member", crewMemberId, "profile"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["run"],
        predicate: query =>
          Array.isArray(query.queryKey) &&
          (query.queryKey.includes("vehicles") || query.queryKey.includes("crew")),
      });
    },
  });
};

export const useTireStrategy = (
  runID: string | undefined,
  vehicleID: string | null
) => {
  const playback = useOptionalPlaybackState();

  return usePlaybackQuery<TireStrategyResponse>({
    runID,
    playback,
    resource: ["tireStrategy", vehicleID ?? "all"],
    fetchFn: async ({ runID, playback: playbackState }) => {
      const response = await galaxieClient.get(
        `/runs/${runID}/vehicles/${vehicleID}/tire_strategy/`,
        {
          params:
            playbackState.mode !== "live" && playbackState.lap !== null
              ? { lap: playbackState.lap }
              : undefined,
        }
      );
      return response.data as TireStrategyResponse;
    },
    options: {
      ...(playback.mode === "live"
        ? QUERY_CONFIG.WEBSOCKET_DATA
        : QUERY_CONFIG.PLAYBACK_DATA),
      enabled: !!runID && !!vehicleID,
    },
  });
};

export const usePlaybackDetails = (runID: string | undefined) => {
  const playback = useOptionalPlaybackState();
  const playbackLap = playback.lap;

  return usePlaybackQuery<PlaybackDetails>({
    runID,
    playback,
    resource: ["playbackDetails"],
    fetchFn: async ({ runID, playback: playbackState }) => {
      const response = await galaxieClient.get(`/runs/${runID}/playback_details/`, {
        params:
          playbackState.mode !== "live" && playbackState.lap !== null
            ? { lap: playbackState.lap }
            : undefined,
      });
      return response.data as PlaybackDetails;
    },
    options: {
      ...(playback.mode === "live"
        ? QUERY_CONFIG.WEBSOCKET_DATA
        : QUERY_CONFIG.PLAYBACK_DATA),
      enabled: !!runID && playbackLap !== null,
    },
  });
};

export const useTelemetryAvailable = (
  runID: string | undefined,
  options: Omit<UseQueryOptions<boolean, Error, boolean>, "queryKey" | "queryFn"> = {}
): UseQueryResult<boolean> => {
  return useQuery<boolean, Error, boolean>({
    queryKey: ["run", runID, "telemetryAvailable"],
    queryFn: async () => {
      if (!runID) {
        return false;
      }
      const response = await galaxieClient.get<{ available: boolean }>(
        `/runs/${runID}/telemetry/available/`
      );
      // Coerce undefined/null to false — React Query's contract requires a
      // defined return value. An empty/malformed response body (or a test
      // galaxieClient mock returning {data: {}}) would otherwise return
      // `undefined` and trigger "Query data cannot be undefined".
      return response.data?.available ?? false;
    },
    enabled: !!runID,
    staleTime: 30 * 60 * 1000, // 30 minutes — static within a session, refreshes across sessions
    gcTime: 300000, // 5 minutes
    ...options,
  });
};

export function useGRCupTelemetry<TQueryFnData = ColumnTable>(
  runID: string,
  options: Omit<
    UseQueryOptions<ColumnTable, Error, TQueryFnData>,
    "queryKey" | "queryFn"
  > = {}
): UseQueryResult<TQueryFnData> {
  const playback = useOptionalPlaybackState();
  const { enabled: userEnabled = true, ...restOptions } = options;

  return usePlaybackQuery<ColumnTable, TQueryFnData>({
    runID,
    playback,
    resource: ["grcupTelemetry"],
    fetchFn: async ({ runID, playback: playbackState }) =>
      galaxieClient
        .get(`/runs/${runID}/telemetry/`, {
          params:
            playbackState.mode !== "live" && playbackState.lap !== null
              ? { lap: playbackState.lap }
              : undefined,
          headers: { Accept: ARROW_MIME_TYPE },
          responseType: "arraybuffer",
        })
        .then(res => asArrowTable(res.data, "grcup_telemetry")),
    options: {
      structuralSharing: false, // ColumnTable loses prototype methods with replaceEqualDeep
      enabled: userEnabled && !!runID,
      ...(playback.mode === "live"
        ? QUERY_CONFIG.WEBSOCKET_DATA
        : QUERY_CONFIG.PLAYBACK_DATA),
      ...restOptions,
    },
  });
}

/**
 * Prefetch utilities for commonly accessed run data
 * Use these in route loaders or component mount to improve perceived performance
 */
export const runPrefetchUtils = {
  /**
   * Prefetch core run data (detail, vehicles, vehicle_laps)
   * Commonly accessed by multiple widgets on the run page
   */
  core: async (queryClient: QueryClient, runID: string) => {
    if (!runID || runID === "undefined") return;
    await Promise.all([
      queryClient.prefetchQuery({
        ...runQueryOptions.detail(runID),
        staleTime: 5 * 60 * 1000, // 5 minutes
      }),
      queryClient.prefetchQuery({
        ...runQueryOptions.vehicles(runID),
        staleTime: 5 * 60 * 1000, // 5 minutes
      }),
      queryClient.prefetchQuery({
        queryKey: ["run", runID, "vehicle_laps", "live"],
        queryFn: async () => {
          // galaxieClient interceptor already parses Arrow → ColumnTable,
          // so just use res.data directly (don't call parseArrowResponse again)
          const response = await galaxieClient.get(`/runs/${runID}/vehicle_laps/`, {
            responseType: "arraybuffer",
            headers: { Accept: ARROW_MIME_TYPE },
          });
          return asArrowTable(response.data, "vehicle_laps");
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
      }),
    ]);
  },

  /**
   * Prefetch telemetry-related data (if telemetry is available)
   */
  telemetry: async (queryClient: QueryClient, runID: string) => {
    if (!runID || runID === "undefined") return;
    // Check if telemetry is available first
    const telemetryAvailable = await queryClient.fetchQuery({
      queryKey: ["run", runID, "telemetryAvailable"],
      queryFn: async () => {
        const response = await galaxieClient.get<{ available: boolean }>(
          `/runs/${runID}/telemetry/available/`
        );
        return response.data.available;
      },
      staleTime: 30 * 60 * 1000, // 30 minutes
    });

    if (telemetryAvailable) {
      // Prefetch telemetry data structure (metadata only, not full data)
      // Full telemetry is loaded on-demand via WebSocket
      await queryClient.prefetchQuery({
        queryKey: ["run", runID, "grcupTelemetry", "live"],
        queryFn: async () => {
          // Return empty table as placeholder - WebSocket will populate
          const aq = await import("arquero");
          return aq.from({});
        },
        staleTime: 0, // WebSocket will update immediately
      });
    }
  },

  /**
   * Prefetch all commonly accessed data for a run
   * Use in route loaders for best performance
   */
  all: async (queryClient: QueryClient, runID: string) => {
    if (!runID || runID === "undefined") return;
    await Promise.all([
      runPrefetchUtils.core(queryClient, runID),
      runPrefetchUtils.telemetry(queryClient, runID),
    ]);
  },
};

/**
 * Fetch championship format metadata for a run.
 * Format is static - cached aggressively since it doesn't change during a race.
 */
export function useRunFormat(
  runID: string,
  options: Omit<
    UseQueryOptions<ChampionshipFormatMeta | null, Error>,
    "queryKey" | "queryFn"
  > = {}
): UseQueryResult<ChampionshipFormatMeta | null> {
  return useQuery({
    queryKey: ["runFormat", runID],
    queryFn: () =>
      galaxieClient
        .get<ChampionshipFormatMeta | null>(`/runs/${runID}/format/`)
        .then(res => res.data),
    staleTime: 30 * 60 * 1000, // 30 minutes — static within a session, refreshes across sessions
    gcTime: 60 * 60 * 1000, // 1 hour
    ...options,
    enabled: !!runID && runID !== "undefined" && (options?.enabled ?? true),
  });
}
